import React, { lazy, Suspense, useEffect, useState } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import './App.css';
import './global.css';
import useWindowSize from './utils/useWindowSize';

const LazyPulseHomePage = lazy(() =>
  import('./components/pulse_ai_homepage/PulseHomePage')
);

const LazyMobileContactSales = lazy(() =>
  import('./components/mobile_view/mobile_contact_sales/MobileContactSales')
);

const LazyMobileCaseStudy = lazy(() =>
  import('./components/mobile_view/mobile_case_study/MobileCaseStudy')
);
const LazyPulseMobileHomePage = lazy(() =>
  import(
    './components/mobile_view/mobile_pulse_ai_homepage/PulseMobileHomePage'
  )
);
const LazyWorkSpace = lazy(() => import('./components/workspace/WorkSpace'));
const LazyLoginPage = lazy(() => import('./components/login-page/LoginPage'));
const LazyCreatedStatusPage = lazy(() =>
  import(
    './components/login-page/sub-components/created-status-page/CreatedStatusPage'
  )
);
const LazyInitiatedStatusPage = lazy(() =>
  import(
    './components/login-page/sub-components/initiated-status-page/InitiatedStatusPage'
  )
);
const LazyResetPasswordPage = lazy(() =>
  import('./components/login-page/sub-components/reset-password/ResetPassword')
);
const LazyAboutUs = lazy(() => import('./components/about-us/AboutUs'));
const LazyChatInterface = lazy(() =>
  import('./components/chatInterface/ChatInterface')
);
const LazyChatHomepage = lazy(() =>
  import('./components/chathomepage/ChatHomePage')
);
const LazyPaymentPlan = lazy(() =>
  import('./components/payment-plan/PaymentPlan')
);
const LazyPaymentScreen = lazy(() =>
  import('./components/payment/PaymentScreen')
);
const LazyPaymentStatus = lazy(() =>
  import('./components/payment-status/PaymentStatus')
);
const LazyResultAnalysisHomePage = lazy(() =>
  import('./components/resultanalysis/ResultAnalysisHomePage')
);
const LazyHeader = lazy(() => import('./components/header/Header'));
const LazyProfileAccount = lazy(() =>
  import('./components/user_profile_account/UserProfileAccount')
);

const NewUserPaymentPlan = lazy(() =>
  import('../src/components/payment-plan/NewUserPaymentPlan')
);
const LazyAllSurveys = lazy(() =>
  import('../src/components/workspace/sub-components/all-surveys/AllSurveys')
);
const LazyPrivacyPolicyPage = lazy(() =>
  import('../src/components/privacy-policy/PrivacyPolicy')
);
const LazyTermsOfUse = lazy(() =>
  import('../src/components/terms-of-use/TermsOfUse')
);

function App() {
  const [status, setStatus] = useState(
    localStorage.getItem('status') || 'CREATED'
  );
  const width = useWindowSize();

  const { account_id } = useParams();
  const accountId = account_id || localStorage.getItem('account_id');

  const [authToken, setAuthToken] = useState(
    () => localStorage.getItem('auth-token') || null
  );

  const location = useLocation();

  useEffect(() => {
    setAuthToken(localStorage.getItem('auth-token') || '');
  }, [location]);

  useEffect(() => {
    setStatus(localStorage.getItem('status') || 'CREATED');
  }, [location]);

  const ProtectedRoute = ({ children }) => {
    const location = useLocation();

    const currentStatus = status || localStorage.getItem('status');

    // Reversed logic here - show PulseHomePage only if status is CREATED

    if (currentStatus === 'CREATED' || width < 500) {
      return children;
    } else {
      // If status is not CREATED, redirect to workspace
      return (
        <Navigate
          to={`/acc/${accountId}/workspace`}
          state={{ from: location }}
          replace
        />
      );
    }
  };

  const WorkspaceRoute = ({ children }) => {
    const location = useLocation();

    // window.location.reload();

    // Show workspace if status is not CREATED, otherwise redirect to home

    if (status !== 'CREATED' && width > 500) { // given provision only when user has in desktop and based on status
      return children;
    } else {
      return <Navigate to='/' state={{ from: location }} replace />;
    }
  };

  return (
    <div className='App'>
      <Routes>
        <Route
          path='/'
          element={
            <Suspense>
              <ProtectedRoute>
                {width < 500 ? (
                  <LazyPulseMobileHomePage status={status} />
                ) : (
                  <LazyPulseHomePage status={status} />
                )}
              </ProtectedRoute>
            </Suspense>
          }
        />
        <Route
          path='/privacy-policy'
          element={
            <Suspense>
              <ProtectedRoute>
                <LazyPrivacyPolicyPage />
              </ProtectedRoute>
            </Suspense>
          }
        />

        <Route
          path='/terms-of-use'
          element={
            <Suspense>
              <ProtectedRoute>
                <LazyTermsOfUse />
              </ProtectedRoute>
            </Suspense>
          }
        />

        <Route
          path='/acc/:account_id'
          element={
            <Suspense>
              <ProtectedRoute>
                {width < 500 ? (
                  <LazyPulseMobileHomePage status={status} />
                ) : (
                  <LazyPulseHomePage status={status} />
                )}
              </ProtectedRoute>
            </Suspense>
          }
        />
        <Route
          path='/acc/:account_id/contact_sales'
          element={
            <Suspense>
              <ProtectedRoute>
                <LazyMobileContactSales status={status} />
              </ProtectedRoute>
            </Suspense>
          }
        />

        <Route
          path='/case-study'
          element={
            <Suspense>
              <LazyMobileCaseStudy />
            </Suspense>
          }
        />
        <Route
          path='/acc/:account_id/workspace'
          element={
            <Suspense>
              <WorkspaceRoute>
                <LazyWorkSpace />
              </WorkspaceRoute>
            </Suspense>
          }
        />

        <Route
          path='/acc/:account_id/token/:token_id'
          element={
            <Suspense>
              <LazyLoginPage />
            </Suspense>
          }
        />

        <Route
          path='/acc/:account_id/created'
          element={
            <Suspense>
              <LazyCreatedStatusPage />
            </Suspense>
          }
        />
        <Route
          path='/acc/:account_id/initiated'
          element={
            <Suspense>
              <LazyInitiatedStatusPage />
            </Suspense>
          }
        />
        <Route
          path='/acc/:account_id/setpassword/verifytoken/:verifytoken/email/:email_id'
          element={
            <Suspense>
              <LazyResetPasswordPage />
            </Suspense>
          }
        />
        <Route
          path='/about-us'
          element={
            <Suspense>
              <LazyAboutUs />
            </Suspense>
          }
        />
        <Route
          path='/acc/:account_id/about-us'
          element={
            <Suspense>
              <LazyAboutUs />
            </Suspense>
          }
        />

        <Route
          path='c/:chat_id?'
          element={
            <Suspense>
              <LazyChatInterface r_chat_history={[]} />
            </Suspense>
          }
        />
        <Route
          path='/acc/:account_id/surveyflow/:s_id/chat/:chat_id'
          element={
            <Suspense>
              <LazyChatHomepage />
            </Suspense>
          }
        />

        <Route
          path='/payment-status-survey/acc/:account_id/surveyflow/:s_id/chat/:chat_id'
          element={
            <Suspense>
              <LazyPaymentStatus payment_survey_state_page={'survey'} />
            </Suspense>
          }
        />

        <Route
          path='/acc/:account_id/surveyflow/:s_id/plans'
          element={
            <Suspense>
              <LazyHeader>
                <NewUserPaymentPlan />
              </LazyHeader>
            </Suspense>
          }
        />

        <Route
          path='/acc/:account_id/payment-plan'
          element={
            <Suspense>
              <LazyPaymentPlan />
            </Suspense>
          }
        />
        <Route
          path='/acc/:account_id/payment'
          element={
            <Suspense>
              <LazyPaymentScreen />
            </Suspense>
          }
        />
        <Route
          path='/acc/:account_id/login'
          element={
            <Suspense>
              <LazyLoginPage />
            </Suspense>
          }
        />
        <Route
          path='/payment-status'
          element={
            <Suspense>
              <LazyPaymentStatus payment_survey_state_page={'account'} />
            </Suspense>
          }
        />

        <Route
          path='/payment-status-account/'
          element={
            <Suspense>
              <LazyPaymentStatus payment_survey_state_page={'account'} />
            </Suspense>
          }
        />

        <Route
          path='acc/:account_id/surveyflow/:s_id/resultchat/:r_chat_id/survey/:surveyId'
          element={
            <Suspense>
              <LazyHeader>
                <LazyResultAnalysisHomePage />
              </LazyHeader>
            </Suspense>
          }
        />
        <Route
          path='/acc/:account_id/profile'
          element={
            <Suspense>
              <LazyProfileAccount />
            </Suspense>
          }
        />
        <Route
          path='/acc/:account_id/billing'
          element={
            <Suspense>
              <LazyProfileAccount />
            </Suspense>
          }
        />
        <Route
          path='/acc/:account_id/pricing'
          element={
            <Suspense>
              <LazyProfileAccount />
            </Suspense>
          }
        />
        <Route
          path='/acc/:account_id/faqs'
          element={
            <Suspense>
              <LazyProfileAccount />
            </Suspense>
          }
        />
        <Route
          path='/acc/:account_id/allsurveys'
          element={
            <Suspense>
              <LazyHeader>
                <LazyAllSurveys />
              </LazyHeader>
            </Suspense>
          }
        />
      </Routes>
    </div>
  );
}

export default App;
<marquee>JAI BABUS</marquee>;
